import { differenceInCalendarDays } from "date-fns";
import type { Date as ProtoDate } from "@/gen/google/type/date_pb";
import { fromProtoDate } from "@/utils/date";

/**
 * EXPIRY_WARNING_DAYS is how many days before a purchase expiry the account is
 * treated as "about to expire": the header shows the date in orange and the
 * layout raises the renewal warning.
 */
export const EXPIRY_WARNING_DAYS = 5;

/**
 * daysUntilExpiry returns the calendar days left before a purchase expiry.
 *
 * Calendar days, not elapsed 24-hour periods: the expiry is a date with no
 * time of day, so counting elapsed periods would make the answer depend on
 * when the page happens to be open - late in the evening, an expiry six days
 * out would count as five and raise the warning a day early.
 *
 * The expiry date itself is 0 and the day after it is -1, so callers can use
 * the sign to tell "expired" from "expiring".
 *
 * @param protoDate - The expiry date, as carried by the API.
 *
 * @returns The number of calendar days left, negative once the date has
 *   passed, or null when no expiry date is set.
 */
export function daysUntilExpiry(protoDate: ProtoDate | undefined): number | null {
  const expiredDate = fromProtoDate(protoDate);
  if (!expiredDate) return null;

  return differenceInCalendarDays(expiredDate, new Date());
}

/**
 * expiryColorClass maps a purchase expiry to the Vuetify text colour that
 * shows how urgent it is.
 *
 * @param protoDate - The expiry date, as carried by the API.
 *
 * @returns "text-red" once expired, "text-orange" within EXPIRY_WARNING_DAYS,
 *   "text-green" while comfortably valid, and "" when no expiry date is set.
 */
export function expiryColorClass(protoDate: ProtoDate | undefined): string {
  const daysLeft = daysUntilExpiry(protoDate);
  if (daysLeft === null) return "";

  if (daysLeft < 0) return "text-red";
  if (daysLeft <= EXPIRY_WARNING_DAYS) return "text-orange";
  return "text-green";
}
